import { assertIsUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type DrawerRelationData,
  type NeighboursDefinition,
  type ReuseEntityDefinition,
  type ReuseLinkDefinition,
} from '@bizzdesign/modeler/models';
import { ArrayHelper } from '@bizzdesign/modeler/utils-canvazz';
import {
  assertIsObjectTypeForMetaModel,
  type BaseObjectDto,
  type BaseRelationDto,
} from '@bizzdesign/sdk-knowledgegraph';
import type { ContentItem } from '@bizzdesign/shared/models';
import {
  converRelationDataToSemanticalRelationData,
  convertBaseRelationToSemanticalRelationData,
  convertObjectToSemanticalData,
  type SemanticRelationInput,
} from './sdk-to-semantics.utils';
/**
 * Converts neighbor relation information into proper link and entity arrays for the modeller.
 * @param neighborRelationsSDK Relation information from the SDK. Includes info on source, target and relation type
 * @param originalElementID ID of the element requesting neighbors
 * @returns neighbor definition with an array of entities and links in separate fields.
 */
export function convertFromSDKNeighborData(
  neighborRelationsSDK: BaseRelationDto[],
  originalElementID: string,
): NeighboursDefinition {
  const objects = neighborRelationsSDK.reduce(
    (accumulator: BaseObjectDto[], relation) => {
      // the original object will always be the source of the relation
      // but this is here for a sanity check should the above change
      if (relation.source && relation.source.id !== originalElementID) {
        accumulator.push(relation.source);
      }
      if (relation.target && relation.target.id !== originalElementID) {
        accumulator.push(relation.target);
      }
      return accumulator;
    },
    [],
  );
  const entities: ReuseEntityDefinition[] =
    ArrayHelper.uniqueArrayOfObjectsByKey(objects, 'id').map((obj) =>
      convertObjectToSemanticalData(obj),
    );

  const links: ReuseLinkDefinition[] = neighborRelationsSDK.map((relation) =>
    convertBaseRelationToSemanticalRelationData(relation),
  );
  const neighbors = {
    entities,
    links,
  };
  return neighbors;
}

export function convertDetailDrawerDataToNeighborData(
  relation: DrawerRelationData,
  object: ContentItem,
  sourceObjectId: string,
): NeighboursDefinition {
  const entity = convertContentItemToReuseEntityDef(object);
  const link = convertContentDetailRelationToReuseLinkDef(
    relation,
    sourceObjectId,
    object.id,
  );

  const neighbors: NeighboursDefinition = {
    entities: [entity],
    links: [link],
  };
  return neighbors;
}

function convertContentDetailRelationToReuseLinkDef(
  relation: DrawerRelationData,
  sourceObjectId: string,
  objectId: string,
): ReuseLinkDefinition {
  const relationData: SemanticRelationInput = {
    id: relation.id,
    name: relation.name,
    roleName: relation.roleName,
    externalId: relation.externalId,
    externalSource: relation.externalSource,
    sourceId: sourceObjectId,
    targetId: objectId,
  };
  return converRelationDataToSemanticalRelationData(relationData);
}

export function convertContentItemToReuseEntityDef(
  object: ContentItem,
): ReuseEntityDefinition {
  assertIsUUID(object.id);
  assertIsObjectTypeForMetaModel('BDCore', object.typeName);
  const sdkObject = {
    id: object.id,
    name: object.name,
    type: object.typeName,
    externalId: object.externalId ?? null,
    externalSource: object.externalSource ?? null,
  };
  return convertObjectToSemanticalData(sdkObject);
}
